import { apiSlice } from './apiSlice';

// Types
export interface DataConnection {
  data_connection_id: string;
  name: string;
  description?: string;
  customer_id: string;
  client_id?: string;
  connection_type: 'MSSQL' | 'POSTGRESQL' | 'MYSQL' | 'ORACLE';
  host: string;
  port: number;
  database_name: string;
  username: string;
  connection_string?: string;
  storage_path?: string;
  medallion_enabled: boolean;
  connection_settings?: string;
  connection_status?: 'ACTIVE' | 'INACTIVE' | 'ERROR';
  last_tested_at?: string;
  last_error?: string;
  created_at: string;
  updated_at?: string;
  created_by: string;
  updated_by?: string;
  status: boolean;
  auth_type: string;
}

export interface DataConnectionsResponse {
  connections: DataConnection[];
  total: number;
  skip: number;
  limit: number;
}

export interface CreateConnectionRequest {
  name: string;
  description?: string;
  customer_id?: string;
  client_id?: string;
  connection_type: 'MSSQL' | 'POSTGRESQL' | 'MYSQL' | 'ORACLE';
  host: string;
  port: number;
  database_name: string;
  username: string;
  password: string;
  connection_string?: string;
  storage_path?: string;
  medallion_enabled?: boolean;
  connection_settings?: string;
  auth_type?: string;
}

export interface UpdateConnectionRequest {
  connection_id: string;
  name?: string;
  description?: string;
  connection_type?: string;
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password?: string;
  connection_string?: string;
  storage_path?: string;
  medallion_enabled?: boolean;
  connection_settings?: string;
  auth_type?: string;
  status?: boolean;
}

export interface TestConnectionRequest {
  connection_type: 'MSSQL' | 'POSTGRESQL' | 'MYSQL' | 'ORACLE';
  host: string;
  port: number;
  database_name: string;
  username: string;
  password: string;
  connection_string?: string;
  connection_settings?: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  connection_time_ms?: number;
  database_version?: string;
  error?: string;
}

export interface ExecuteQueryRequest {
  connection_id: string;
  sql_query: string;
  password: string;
  limit?: number;
}

export interface ExecuteQueryResponse {
  success: boolean;
  data: any[];
  columns: string[];
  row_count: number;
  execution_time_ms: number;
}

export interface SearchConnectionsParams {
  q: string;
  skip?: number;
  limit?: number;
}

export const dataConnectionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get all data connections with optional filters
    getAllDataConnections: builder.query<
      DataConnectionsResponse,
      {
        connection_id?: string;
        connection_type?: string;
        connection_status?: string;
        search?: string;
        skip?: number;
        limit?: number;
        customer_id?: string;
        client_id?: string;
      }
    >({
      query: (params) => ({
        url: `/api/v1/data-connections/`,
        method: 'GET',
        params,
      }),
      providesTags: ['DataConnections'],
    }),

    // Get single data connection by ID
    getDataConnectionById: builder.query<DataConnection, string>({
      query: (connectionId) => ({
        url: `/api/v1/data-connections/${connectionId}`,
        method: 'GET',
      }),
      providesTags: (result, error, connectionId) => [{ type: 'DataConnection', id: connectionId }],
    }),
    // Get data connections by customer ID
    getDataConnectionsByCustomerId: builder.query<DataConnectionsResponse, string>({
      query: (customerId) => ({
        url: `/api/v1/data-connections/`,
        method: 'GET',
        params: { customer_id: customerId },
      }),
      providesTags: ['DataConnections'],
    }),

    // Get data connections by client ID
    getDataConnectionsByClientId: builder.query<DataConnectionsResponse, string>({
      query: (clientId) => ({
        url: `/api/v1/data-connections/`,
        method: 'GET',
        params: { client_id: clientId },
      }),
      providesTags: ['DataConnections'],
    }),

    // Get connection password
    getConnectionPassword: builder.query<{ password: string }, string>({
      query: (connectionId) => `/api/v1/data-connections/${connectionId}/password`,
    }),

    // Create new data connection
    createDataConnection: builder.mutation<{ message: string; connection: DataConnection }, CreateConnectionRequest>({
      query: (newConnection) => ({
        url: `/api/v1/data-connections/`,
        method: 'POST',
        body: newConnection,
      }),
      invalidatesTags: ['DataConnections'],
    }),

    // Update existing data connection
    updateDataConnection: builder.mutation<{ message: string; connection: DataConnection }, UpdateConnectionRequest>({
      query: ({ connection_id, ...updatedConnection }) => ({
        url: `/api/v1/data-connections/${connection_id}`,
        method: 'PUT',
        body: updatedConnection,
      }),
      invalidatesTags: (result, error, { connection_id }) => [
        { type: 'DataConnection', id: connection_id },
        'DataConnections',
      ],
    }),

    // Delete data connection (soft delete)
    deleteDataConnection: builder.mutation<{ message: string }, string>({
      query: (connectionId) => ({
        url: `/api/v1/data-connections/${connectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, connectionId) => [
        { type: 'DataConnection', id: connectionId },
        'DataConnections',
      ],
    }),

    // Test new connection (before saving)
    testConnection: builder.mutation<TestConnectionResponse, TestConnectionRequest>({
      query: (connectionData) => ({
        url: `/api/v1/data-connections/test`,
        method: 'POST',
        body: connectionData,
      }),
    }),

    // Test existing saved connection
    testExistingConnection: builder.mutation<TestConnectionResponse, { connection_id: string; password: string }>({
      query: ({ connection_id, password }) => ({
        url: `/api/v1/data-connections/${connection_id}/test`,
        method: 'POST',
        body: { password },
      }),
      invalidatesTags: (result, error, { connection_id }) => [{ type: 'DataConnection', id: connection_id }],
    }),

    // Search data connections
    searchDataConnections: builder.query<DataConnectionsResponse & { search_query: string }, SearchConnectionsParams>({
      query: (params) => ({
        url: `/api/v1/data-connections/search`,
        method: 'GET',
        params,
      }),
      providesTags: ['DataConnections'],
    }),
  }),
});

export const {
  useGetAllDataConnectionsQuery,
  useGetDataConnectionByIdQuery,
  useGetDataConnectionsByCustomerIdQuery,
  useGetDataConnectionsByClientIdQuery,
  useCreateDataConnectionMutation,
  useUpdateDataConnectionMutation,
  useDeleteDataConnectionMutation,
  useTestConnectionMutation,
  useTestExistingConnectionMutation,
  useSearchDataConnectionsQuery,
  useGetConnectionPasswordQuery,
} = dataConnectionApiSlice;
